import { useQuery, useMutation } from 'react-query';
import {
  addMinistryFocusLogic,
  getCustomerPermissions,
  getCustomerProfileData,
  getOrganizationPermissions,
  getProfileData,
} from '../api/userApi';
import { useSelector } from 'react-redux';
import { getOrganization } from '../../selectors/OrgManagementSelectors';

/**
 * Create a selector function to find a single permission from permissions data
 * @param {Object} options - Filter options
 * @param {string} options.permission - Permission displayId to filter by
 * @param {string} options.accountNumber - Account number to filter by
 * @returns {Function} Selector function for react-query
 */
export function getSinglePermission({
  permission,
  accountNumber,
} = {}) {
  return (data) => {
    if (!data) return null;

    const found = data.find((perm) => {
      const matchesDisplay = permission ? perm.displayId === permission : true;
      const matchesAccount = accountNumber ? perm?.permissionScope?.account === accountNumber : true;
      return matchesDisplay && matchesAccount;
    });

    return found || null;
  };
}

const userQueries = {
  base: () => ({
    queryKey: ['user'],
  }),
  permissions: (customerId, linkedOrgs) => ({
    queryKey: [...userQueries.base().queryKey, 'permissions'],
    queryFn: () => getCustomerPermissions(customerId, linkedOrgs),
  }),
  permission: (customerId, organziation) => ({
    queryKey: [...userQueries.base().queryKey, 'permissions', organziation],
    queryFn: () => getOrganizationPermissions(customerId, organziation),
  })
};

/* User Profile */
export function useGetUserProfile() {
  const {
    data: profileResponse,
    isLoading: isProfileLoading,
    isError: isProfileError,
  } = useQuery({
    queryKey: ['user', 'profile'],
    queryFn: () => getProfileData(),
    staleTime: Infinity,
    retry: true,
  });

  return {
    data: profileResponse,
    isLoading: isProfileLoading,
    isError: isProfileError,
  };
}

/* [NEW] User Profile */
export function useGetIndividualProfile() {
  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  } = useQuery({
    queryKey: ['customer', 'user', 'profile'],
    queryFn: () => getCustomerProfileData(),
    staleTime: Infinity,
    retry: false,
  });

  return {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError,
  };
}

/* Add Ministry Focus */
export function useAddMinistryFocus() {
  return useMutation({
    mutationFn: addMinistryFocusLogic,
    onSuccess: async (data) => {
      return { data };
    },
    onError: (error) => {
      return error;
    },
  });
}

export function useGetCustomerPermissions(
  customerId,
  organizationId,
  { select } = {}
) {
  const organization = useSelector((state) =>
    getOrganization(state, organizationId)
  );

  const { data: permissions, isLoading, isError } = useQuery({
    ...userQueries.permission(customerId, organization),
    enabled: !!customerId && !!organization,
    staleTime: Infinity,
    select,
  });

  return {
    data: permissions,
    isLoading: isLoading,
    isError: isError,
  };
}
